import type { CustomerId } from '@gh/common/models'
import { prefetchReusableBridgePolicyCoalesced } from '@/bridge/prefetchReusableBridgePolicyCoalesced'
import { prefetchFollowupBookingContextCoalesced } from '@/components/consultation/prefetchFollowupBookingContextCoalesced'
import { useClinicalConsultationBookingStore } from '@/stores/clinicalConsultationBooking'
import { useProductsStore } from '@/stores/products'
import { preloadStripeClient } from '@/stripe/loadStripeClient'

const CLIENT_PREWARM_COALESCE_MS = 60_000
const COALESCE_WINDOW_MS = CLIENT_PREWARM_COALESCE_MS - 1_000

let lastPrewarmAt = Number.NEGATIVE_INFINITY
let lastPrewarmKey: string | null = null

export function resetPrewarmClinicalBookingClientDataCoalesce(): void {
  lastPrewarmAt = Number.NEGATIVE_INFINITY
  lastPrewarmKey = null
}

export type PrewarmClinicalBookingClientDataOptions = {
  customerId?: CustomerId | null
  /** Start reusable Bridge policy reads (no patient ensure or policy create). */
  prefetchBridgePolicy?: boolean
  /** Start follow-up eligibility read when hub data is not staged yet. */
  prefetchFollowup?: boolean
  /** Apply follow-up prefetch into the booking store when it settles. */
  stageFollowupOnPrefetch?: boolean
}

function buildPrewarmKey(options: PrewarmClinicalBookingClientDataOptions): string {
  const customerId = options.customerId ?? ''
  const flags = [
    options.prefetchBridgePolicy ? 'b' : '',
    options.prefetchFollowup ? 'f' : '',
    options.stageFollowupOnPrefetch ? 's' : '',
  ].join('')
  return `${customerId}:${flags}`
}

/** Idempotent client-side booking warmups shared by hub, plan picker, and flow host. */
export function prewarmClinicalBookingClientData(
  options: PrewarmClinicalBookingClientDataOptions = {},
): void {
  const now = Date.now()
  const key = buildPrewarmKey(options)
  if (key === lastPrewarmKey && now - lastPrewarmAt < COALESCE_WINDOW_MS) {
    return
  }
  lastPrewarmAt = now
  lastPrewarmKey = key

  const prewarmCustomerId = options.customerId ?? null

  preloadStripeClient()
  useProductsStore()

  const bookingStore = useClinicalConsultationBookingStore()
  void bookingStore.ensureLicensedStatesLoaded()

  if (prewarmCustomerId && options.prefetchBridgePolicy) {
    void prefetchReusableBridgePolicyCoalesced(prewarmCustomerId).catch(() => undefined)
  }

  if (!options.prefetchFollowup || !prewarmCustomerId) {
    return
  }

  void prefetchFollowupBookingContextCoalesced(prewarmCustomerId)
    .then((context) => {
      if (!options.stageFollowupOnPrefetch) {
        return
      }
      const liveCustomerId = bookingStore.customer?.id ?? null
      if (liveCustomerId !== prewarmCustomerId) {
        return
      }
      bookingStore.stageFollowupBookingFromHub(context, prewarmCustomerId)
    })
    .catch(() => undefined)
}
